import { DashboardModule } from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { sanitizeFilters } from '@functions/entity'

import useOrders from '@hooks/useOrders'
import useCurrentUser from '@hooks/useCurrentUser'
import useSelector from '@hooks/useSelector'

import type { OrderModel, OrderRequestOptions } from '@models/types'

import { getCampaignPermissions, getCampaignType } from '@functions/campaign'
import useCampaign from '@hooks/useCampaign'
import BlankState from './OrdersBlankState'
import Order from './Order'
import { getDueDateTimestamp } from './functions/sortByDueDate'

type Filters = {
  campaign_id?: number,
  campaign_marketing_type?: string,
  limit?: number,
  order_type?: string,
  priority_orders_by_user_id?: number,
  status_group_key?: string,
}

type OrdersContainerProps = {
  displayMode: string | undefined,
  queryParams?: Filters,
  isCampaignManager: boolean,
  requestOptions: OrderRequestOptions,
}

const OrdersContainer = (props: OrdersContainerProps) => {
  const {
    displayMode, queryParams, isCampaignManager, requestOptions,
  } = props

  // @ts-expect-error TS2339
  const { campaign_id: campaignId, limit } = queryParams

  const { campaign } = useCampaign({ id: campaignId }, { performHttpRequests: !!campaignId })
  const currentUserPayload = useCurrentUser()
  const { currentUser, isAgent } = currentUserPayload

  const campaignPermissions = getCampaignPermissions({ user: currentUser, campaignType: getCampaignType(campaign) })
  const canManageOrders = campaignPermissions?.orders?.manage !== false

  // @ts-expect-error TS2345
  const filters = sanitizeFilters(queryParams)

  const ordersPayload = useOrders({
    filters: {
      ...filters,
      order_by: 'id desc',
    },
    performHttpRequests: !isCampaignManager || !!campaignId,
    requestOptions: {
      include_order_campaign: true,
      include_order_item_due_date: true,
      include_order_items: true,
      include_order_priority_campaign_user_ids: !isAgent,
      include_order_publication: true,
      include_order_publication_edition: true,
      ...requestOptions,
    },
  })

  const {
    filteredOrders,
    hasFilteredOrders,
    loading,
  } = ordersPayload

  const orderItems = useSelector(reduxState => reduxState.entities.orderItems)

  const getOrderDueDateTimestamp = (order: OrderModel) => Math.min(
    ...(order.items?.map(orderItemId => getDueDateTimestamp(orderItems[orderItemId])) || [Infinity]),
  )

  const ordersArray = [...filteredOrders]
    .sort((a, b) => getOrderDueDateTimestamp(a) - getOrderDueDateTimestamp(b))
    .slice(0, limit || 50)

  return (
    <DashboardModule.ScrollableSection>
      {!loading && ordersArray?.map((order: OrderModel) => (
        <Order
          canManageOrders={canManageOrders}
          // @ts-expect-error TS2322
          currentUser={currentUser}
          // @ts-expect-error TS2322
          displayMode={displayMode}
          isCampaignManager={isCampaignManager}
          key={order.id}
          order={order}
        />
      ))}

      {!loading && !hasFilteredOrders && (<BlankState />)}
    </DashboardModule.ScrollableSection>
  )
}

export default OrdersContainer
